import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PurchaseOrderService } from 'src/app/services/purchase-order.service';
import { PurchasesService } from 'src/app/services/purchases.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { ButtonLoading } from 'src/app/models/button-loading.model';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { getLongDate, getStringDate } from 'src/app/common/util';
import { PurchaseOrderDetailResponse } from 'src/app/models/purchase-order.model';
import { PurchasesAssociationService } from 'src/app/services/purchases-association.service';
import { catchError, map } from 'rxjs/operators';
import { ModalDialogComponent } from 'src/app/components/modals/modal-dialog/modal-dialog.component';

type OrderVM = {
  id: number;
  number: string;
  providerName: string;
  date: number | string;
  total: number;
  associatedPurchaseId?: number | null;
  enteredIntoInventory: boolean;
};

type PurchaseVM = {
  id: number;
  number: string;
  providerName: string;
  date: number | string;
  total: number;
};

@Component({
  selector: 'app-purchase-association',
  templateUrl: './purchase-association.component.html',
  styleUrls: ['./purchase-association.component.scss']
})
export class PurchaseAssociationComponent {
  loading = false;

  allOrders: OrderVM[] = [];
  allPurchases: PurchaseVM[] = [];

  orders: OrderVM[] = [];
  purchases: PurchaseVM[] = [];

  filterOrders = '';
  filterPurchases = '';

  startDate = '';
  endDate = '';

  selectedPurchaseId: number | null = null;
  initialRelatedOrderIdsBefore: number[] = [];
  initialRelatedOrderIds: number[] = [];
  currentRelatedOrderIds: number[] = [];

  buttonAssociate: ButtonLoading = new ButtonLoading('btn-success', false, 'GUARDAR', 'fa-save');

  paginationOrders = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationPurchases = new SimplePaginationModel(0, 5, 0, 0, 0);

  currentOrderDetails: PurchaseOrderDetailResponse[] = [];
  currentPurchases = [];

  baseOrders: OrderVM[] = [];

  allPurchasesNotAssociated: PurchaseVM[] = [];
  allPurchasesAssociated: PurchaseVM[] = [];

  purchasesNotAssociated: PurchaseVM[] = [];
  purchasesAssociated: PurchaseVM[] = [];

  paginationPurchasesNotAssociated = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationPurchasesAssociated = new SimplePaginationModel(0, 5, 0, 0, 0);

  transformation = false;

  private timeoutOrders: any = null;
  private timeoutPurchases: any = null;

  private associatedOrdersCache = new Map<number, OrderVM[]>();
  loadingOrdersDetails = false;
  showModal = false;

  readonly transformationProviderName = 'Fusiones Alimenticias, S.A.';
  readonly transformationCodes: string[] = ['XMLR0016','XMLR0015','XMLR0017'];
  showTransformationSwitch = false;

  @ViewChild('svgContainer', { static: true }) svgContainer: ElementRef;
  @HostListener('window:resize') onResize() { this.drawMultipleLinks(); }

  constructor(
    private toastService: ToastrService,
    private purchaseOrderService: PurchaseOrderService,
    private purchasesAssociationService: PurchasesAssociationService,
    private purchasesService: PurchasesService
  ) {
    const today = new Date();
    this.startDate = getStringDate(today);
    this.endDate = getStringDate(today);
  }

  ngOnInit(): void {
    this.loadStartData();
  }

  /** Carga inicial */
  loadStartData() {
    this.loadPurchases();
    this.reloadOrders();
  }

  private normalizePurchase(p: any): PurchaseVM {
    const composedNumber = p.series ? `${p.series}-${p.number}` : String(p.number);
    return {
      id: p.id,
      number: composedNumber,
      providerName: p.providerData?.name ?? '—',
      date: p.date ?? p.accountingDate,
      total: p.totalCost ?? 0
    };
  }

  private normalizeOrder(o: any): OrderVM {
    return {
      id: o.id,
      number: String(o.number),
      providerName: o.providerData?.name ?? '—',
      date: o.creationDate,
      total: 0,
      associatedPurchaseId: o.associatedPurchaseId ?? null,
      enteredIntoInventory: o.enteredIntoInventory
    };
  }

  /** Cargar facturas (lado izquierdo) */
  loadPurchases() {
    const start = getLongDate(this.startDate);
    const end = getLongDate(this.endDate);

    this.purchasesService.getPurchasesByIntervalDateNotAssociated(start, end).subscribe({
      next: (resp: ResponseObject) => {
        this.allPurchasesNotAssociated = (resp.data || []).map((p: any) => this.normalizePurchase(p));
        this.refreshPurchasesPaginationNotAssociated();
      },
      error: () => this.toastService.error('Error al obtener compras no asociadas')
    });

    // En la misma llamada puedes cargar las asociadas también
    this.purchasesService.getPurchasesByIntervalDateAssociated(start, end).subscribe({
      next: (resp: ResponseObject) => {
        this.allPurchasesAssociated = (resp.data || []).map((p: any) => this.normalizePurchase(p));
        this.refreshPurchasesPaginationAssociated();
      },
      error: () => this.toastService.error('Error al obtener compras asociadas')
    });
  }

  /** Cargar órdenes (lado derecho) */
  reloadOrders() {
    this.purchaseOrderService.getOrders().subscribe({
      next: (resp: ResponseObject) => {
        this.baseOrders = (resp.data || [])
          .map((o: any) => this.normalizeOrder(o))
          .sort((a, b) => Number(b.number) - Number(a.number));
        this.resetAllOrdersToBase();
        this.refreshOrdersPagination();
      },
      error: () => this.toastService.error('Error al obtener órdenes')
    });
  }

  /** Seleccionar una compra => carga órdenes asociadas */
  selectPurchase(purchaseId: number) {
    if (this.selectedPurchaseId === purchaseId) return;
    this.transformation = false;
    this.showTransformationSwitch = false;


    this.selectedPurchaseId = purchaseId;
    this.getPurchaseDetails();

    this.purchasesAssociationService.getRelatedOrdersByPurchase(purchaseId).subscribe({
      next: (resp: ResponseObject) => {
        const relatedIds: number[] = (resp.data || []).map((item: any) => item.id);

        this.initialRelatedOrderIds = [...relatedIds];
        this.initialRelatedOrderIdsBefore = [...relatedIds];
        this.currentRelatedOrderIds = [...relatedIds];

        this.resetAllOrdersToBase();
        this.upsertAssociatedIntoAllOrders(resp.data || [], purchaseId);

        this.refreshOrdersPagination();
        this.getOrdersDetails();
        this.drawMultipleLinks();
      },
      error: () => this.toastService.error('Error al obtener asociaciones de la compra')
    });
  }

  getPurchaseDetails() {
    
    if(!this.selectedPurchaseId)
      return;

    
    
    this.purchasesService.getDetailPurchaseInvoice(this.selectedPurchaseId).subscribe({
      next: (resp: ResponseObject) => {
        this.currentPurchases = resp.data || [];
        this.showTransformationSwitch = this.computeTransformationAvailability(this.currentPurchases);

        if (!this.showTransformationSwitch) {
          this.transformation = false;
        }

      },
      error: () => this.toastService.error('Error al obtener detalles de la orden')
    });
  }

  getOrdersDetails() {
    this.currentOrderDetails = [];
    
    if(!this.currentRelatedOrderIds || this.currentRelatedOrderIds.length == 0)
      return;

    this.loadingOrdersDetails = true;

    const calls = this.currentRelatedOrderIds.map(id =>
      this.purchaseOrderService.getOrder(id).pipe(
        map((resp: ResponseObject) => resp.data || []),
        catchError(() => of([]))
      )
    );

    forkJoin(calls).subscribe({
      next: (arrays) => {
        const joined = ([] as any[]).concat(...arrays);

        joined.sort((a, b) => a.id - b.id);

        this.currentOrderDetails = joined;
        this.loadingOrdersDetails = false;
        
      },
      error: () => {
        this.toastService.error('Error al obtener detalles de compras', 'Error');
        this.loadingOrdersDetails = false;
      }
    });
    
  }

  /** Regla: una orden solo puede estar asociada a UNA compra */
  isOrderLocked(o: OrderVM): boolean {
    if (!o.associatedPurchaseId) return false;
    return this.selectedPurchaseId != null && o.associatedPurchaseId !== this.selectedPurchaseId;
  }

  /** Click en orden: alterna selección */
  toggleOrder(o: OrderVM) {
    if (!this.selectedPurchaseId) return;

    if (this.isOrderLocked(o)) {
      this.toastService.warning('Esta orden ya está asociada a otra compra.');
      return;
    }

    if (this.isTransformationMode && this.isTryingToAddSecondOrder(o.id)) {
      this.toastService.warning('Con Transformación activa solo se permite asociar 1 orden a 1 compra.');
      return;
    }

    const idx = this.currentRelatedOrderIds.indexOf(o.id);
    if (idx === -1) {
      this.currentRelatedOrderIds.push(o.id);
      o.associatedPurchaseId = this.selectedPurchaseId;
    } else {
      this.currentRelatedOrderIds.splice(idx, 1);
      if (o.associatedPurchaseId === this.selectedPurchaseId) {
        o.associatedPurchaseId = null;
      }
    }

    this.refreshOrdersPagination();
    this.getOrdersDetails();
    this.drawMultipleLinks();
  }

  getDisabledSave(): boolean {
    const initialSet = new Set(this.initialRelatedOrderIdsBefore);
    const currentSet = new Set(this.currentRelatedOrderIds);

    const toAdd = this.currentRelatedOrderIds.some(id => !initialSet.has(id));
    const toRemove = this.initialRelatedOrderIdsBefore.some(id => !currentSet.has(id));

    return !(toAdd || toRemove) || !this.selectedPurchaseId || this.buttonAssociate.loading;
  }



  saveAssociation(finalize: boolean) {
    if (!this.selectedPurchaseId) return;

    if (this.isTransformationMode && this.currentRelatedOrderIds.length !== 1) {
      this.toastService.error('Con Transformación activa debes asociar exactamente 1 orden.');
      return;
    }


    const purchaseId = this.selectedPurchaseId;

    const initialSet = new Set(this.initialRelatedOrderIdsBefore);
    const currentSet = new Set(this.currentRelatedOrderIds);

    const toAdd = this.currentRelatedOrderIds.filter(id => !initialSet.has(id));
    const toRemove = this.initialRelatedOrderIdsBefore.filter(id => !currentSet.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      this.toastService.info('No hay cambios por guardar.');
      return;
    }

    this.buttonAssociate.loading = true;

    this.showModal = false;
    this.loading = true;
    const ops = [
      ...toAdd.map(oid => this.purchasesAssociationService.createRelationPurchaseOrderByInvoice(oid, purchaseId,this.transformation)),
      ...toRemove.map(oid => this.purchasesAssociationService.deleteRelationPurchaseOrder(oid, purchaseId)),
    ];

    forkJoin(ops).subscribe({
      next: () => {
        this.toastService.success('Asociaciones actualizadas correctamente', 'Éxito');
        this.buttonAssociate.loading = false;
        this.updateInventoriesForOrderByInvoice(purchaseId, finalize);
        this.selectPurchase(purchaseId);
        this.reloadOrders();
      },
      error: (e: HttpErrorResponse) => {
        this.toastService.error(e?.error?.data.description || 'Error al actualizar asociaciones', 'Error');
        this.buttonAssociate.loading = false;
        this.loading = false;
      }
    });
  }

  updateInventoriesForOrderByInvoice(purchaseId: number, finalized: boolean) {
    const data = {
      finalize: finalized,
      transformation: this.transformation
    };
    this.purchasesAssociationService.putInventoriesForOrderByInvoice(purchaseId, data).subscribe({
      next: (response: ResponseObject) => {
        if (response.data) this.toastService.success('Inventarios actualizados correctamente', 'Éxito');
        this.loading = false;
      },
      error: (e: HttpErrorResponse) => {
        this.toastService.error(e?.error?.message || 'Error al actualizar inventarios', 'Error');
        this.loading = false;
      }
    });
  }

  /** Helpers internos */
  private resetAllOrdersToBase() {
    this.allOrders = this.baseOrders.map(o => ({ ...o }));
  }

  private upsertAssociatedIntoAllOrders(rawAssociated: any[], purchaseId: number) {
    const normalized: OrderVM[] = (rawAssociated || []).map(o => {
      const vm = this.normalizeOrder(o);
      vm.associatedPurchaseId = purchaseId;
      return vm;
    });

    this.associatedOrdersCache.set(purchaseId, normalized);

    const byId = new Map<number, OrderVM>();
    for (const o of this.allOrders) byId.set(o.id, o);
    for (const o of normalized) byId.set(o.id, o);

    this.allOrders = Array.from(byId.values());
  }

  drawMultipleLinks() {
    const svg = this.svgContainer?.nativeElement as SVGElement;
    if (!svg) return;

    svg.innerHTML = '';

    if (!this.selectedPurchaseId || this.currentRelatedOrderIds.length === 0) return;

    setTimeout(() => {
      const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();

      const selectedRow =
        (document.querySelector('#table-purchases-not-associated tr.selected') as HTMLElement) ||
        (document.querySelector('#table-purchases-associated tr.selected') as HTMLElement);

      if (!selectedRow) {
        console.warn('⚠️ No se encontró fila seleccionada en ninguna tabla de compras');
        return;
      }

      const selectedRect = selectedRow.getBoundingClientRect();
      const x1 = selectedRect.right - parentRect.left - 12;
      const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;

      for (const oid of this.currentRelatedOrderIds) {
        const relatedRow = document.querySelector(`#table-orders tr[data-id="${oid}"]`) as HTMLElement;
        if (!relatedRow) continue;

        const rect = relatedRow.getBoundingClientRect();
        const x2 = rect.left - parentRect.left - 12;
        const y2 = rect.top + rect.height / 2 - parentRect.top;
        const xMiddle = x1 + ((x2 - x1) / 2);

        this.drawLine(svg, x1, xMiddle, y1, y1);
        this.drawLine(svg, xMiddle, x2, y2, y2);
        this.drawLine(svg, xMiddle, xMiddle, y1, y2);
      }
    }, 50);
  }


  private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#001f54');
    line.setAttribute('stroke-width', '2');
    svg.appendChild(line);
  }

  debouncedFilterOrders() {
    clearTimeout(this.timeoutOrders);
    this.timeoutOrders = setTimeout(() => {
      this.paginationOrders.page = 0;
      this.refreshOrdersPagination();
    }, 250);
  }
  
  refreshOrdersPagination() {
    const q = (this.filterOrders || '').toLowerCase();
    const matches = (o: OrderVM) =>
      (o.number || '').toLowerCase().includes(q) ||
      (o.providerName || '').toLowerCase().includes(q);

    const associatedSet = new Set(this.currentRelatedOrderIds);

    // órdenes que ya están asociadas a la compra seleccionada (pinned arriba)
    const pinned = this.allOrders.filter(o => associatedSet.has(o.id));

    // resto de órdenes filtradas
    const restRaw = this.allOrders.filter(o => !associatedSet.has(o.id) && matches(o));

    // prioridad: libres primero, luego asociadas a otra compra (locked)
    const score = (x: OrderVM) => {
      if (!x.associatedPurchaseId) return 1;
      return 2;
    };
    const rest = restRaw.sort((a, b) => score(a) - score(b));

    const final = [...pinned, ...rest];

    this.paginationOrders.total = final.length;
    this.paginationOrders.start = this.paginationOrders.page * this.paginationOrders.size;
    const end = this.paginationOrders.start + this.paginationOrders.size;
    this.paginationOrders.end = Math.min(end, final.length);

    this.orders = final.slice(this.paginationOrders.start, this.paginationOrders.end);

    this.drawMultipleLinks();
  }


  /** Paginación / filtros — Compras */
  debouncedFilterPurchases() {
    clearTimeout(this.timeoutPurchases);
    this.timeoutPurchases = setTimeout(() => {
      this.paginationPurchases.page = 0;
      this.refreshPurchasesPagination();
    }, 250);
  }

  refreshPurchasesPagination() {
    const q = (this.filterPurchases || '').toLowerCase();
    const filtered = this.allPurchases.filter(p =>
      (p.number || '').toLowerCase().includes(q) ||
      (p.providerName || '').toLowerCase().includes(q)
    );

    this.paginationPurchases.total = filtered.length;
    this.paginationPurchases.start = this.paginationPurchases.page * this.paginationPurchases.size;
    const end = this.paginationPurchases.start + this.paginationPurchases.size;
    this.paginationPurchases.end = Math.min(end, filtered.length);

    this.purchases = filtered.slice(this.paginationPurchases.start, this.paginationPurchases.end);

    this.drawMultipleLinks();
  }

  refreshPurchasesPaginationNotAssociated() {
    const q = (this.filterPurchases || '').toLowerCase();
    const filtered = this.allPurchasesNotAssociated.filter(p =>
      (p.number || '').toLowerCase().includes(q) ||
      (p.providerName || '').toLowerCase().includes(q)
    );

    this.paginationPurchasesNotAssociated.total = filtered.length;
    this.paginationPurchasesNotAssociated.start = this.paginationPurchasesNotAssociated.page * this.paginationPurchasesNotAssociated.size;
    const end = this.paginationPurchasesNotAssociated.start + this.paginationPurchasesNotAssociated.size;
    this.paginationPurchasesNotAssociated.end = Math.min(end, filtered.length);

    this.purchasesNotAssociated = filtered.slice(this.paginationPurchasesNotAssociated.start, this.paginationPurchasesNotAssociated.end);
  }

  refreshPurchasesPaginationAssociated() {
    const q = (this.filterPurchases || '').toLowerCase();
    const filtered = this.allPurchasesAssociated.filter(p =>
      (p.number || '').toLowerCase().includes(q) ||
      (p.providerName || '').toLowerCase().includes(q)
    );

    this.paginationPurchasesAssociated.total = filtered.length;
    this.paginationPurchasesAssociated.start = this.paginationPurchasesAssociated.page * this.paginationPurchasesAssociated.size;
    const end = this.paginationPurchasesAssociated.start + this.paginationPurchasesAssociated.size;
    this.paginationPurchasesAssociated.end = Math.min(end, filtered.length);

    this.purchasesAssociated = filtered.slice(this.paginationPurchasesAssociated.start, this.paginationPurchasesAssociated.end);
  }

  closeModal(modal: ModalDialogComponent) {
    modal.onClose();
    this.showModal = false;
    // this.closed.emit(true);
  }

  computeTransformationAvailability(details: any[]): boolean {
    if (!this.selectedPurchaseId) return false;
    if (!details || details.length === 0) return false;

    const providerFromList =
      (this.purchasesNotAssociated.find(p => p.id === this.selectedPurchaseId)?.providerName) ||
      (this.purchasesAssociated.find(p => p.id === this.selectedPurchaseId)?.providerName) ||
      '';

    const providerOk =
      providerFromList.trim().toLowerCase() === this.transformationProviderName.trim().toLowerCase();

    if (!providerOk) return false;

    const codesSet = new Set(this.transformationCodes.map(x => (x || '').trim().toUpperCase()));

    const hasAnyProduct =
      details.some(d => codesSet.has(((d?.productData?.code) || '').trim().toUpperCase()));

    return hasAnyProduct;
  }


  get isTransformationMode(): boolean {
    return this.showTransformationSwitch && this.transformation === true;
  }

  private isTryingToAddSecondOrder(orderIdToAdd: number): boolean {
    const alreadySelected = this.currentRelatedOrderIds.includes(orderIdToAdd);
    if (alreadySelected) return false;
    return this.currentRelatedOrderIds.length >= 1;
  }

  onTransformationToggle() {
    if (this.isTransformationMode && this.currentRelatedOrderIds.length > 1) {
      this.currentRelatedOrderIds = [this.currentRelatedOrderIds[0]];
      this.refreshOrdersPagination();
      this.getOrdersDetails();
      this.drawMultipleLinks();

      this.toastService.info('Se dejó solo 1 orden asociada porque Transformación requiere 1 a 1.');
    }
  }


}
